import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Search, TrendingUp, Users, Loader2, Hash, FileText, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { FollowButton } from '@/components/ui/FollowButton'
import { searchApi, User, Hashtag } from '@/lib/api'
import { getInitials, debounce } from '@/lib/utils'
import { formatDistanceToNow } from 'date-fns'

type SearchType = 'all' | 'users' | 'posts' | 'hashtags'

export function ExplorePage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [activeTab, setActiveTab] = useState<SearchType>('all')
  const [recentSearches, setRecentSearches] = useState<string[]>([])

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('recentSearches')
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored))
      } catch (e) {
        console.error('Failed to parse recent searches', e)
      }
    }
  }, [])

  const debouncedSearch = debounce((query: string) => {
    setDebouncedQuery(query)
    if (query.trim().length >= 2) {
      // Save to recent searches
      const updated = [query, ...recentSearches.filter((s) => s !== query)].slice(0, 5)
      setRecentSearches(updated)
      localStorage.setItem('recentSearches', JSON.stringify(updated))
    }
  }, 300)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery(value)
    debouncedSearch(value)
  }

  const clearSearch = () => {
    setSearchQuery('')
    setDebouncedQuery('')
  }

  const selectRecentSearch = (query: string) => {
    setSearchQuery(query)
    setDebouncedQuery(query)
  }

  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['search', debouncedQuery, activeTab],
    queryFn: () => searchApi.search(debouncedQuery, activeTab),
    enabled: debouncedQuery.length >= 2,
  })

  const { data: trendingHashtags, isLoading: trendingLoading } = useQuery({
    queryKey: ['trending-hashtags'],
    queryFn: () => searchApi.getTrendingHashtags(10),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  const hasResults =
    searchResults &&
    ((searchResults.data?.users?.length ?? 0) > 0 ||
      (searchResults.data?.posts?.length ?? 0) > 0 ||
      (searchResults.data?.hashtags?.length ?? 0) > 0)

  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl font-bold">Explore</h1>

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search people, posts, and hashtags..."
          className="pl-9 pr-10"
          value={searchQuery}
          onChange={handleSearchChange}
        />
        {searchQuery && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Recent Searches */}
      {!debouncedQuery && recentSearches.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <h3 className="mb-3 text-sm font-medium text-muted-foreground">Recent Searches</h3>
            <div className="flex flex-wrap gap-2">
              {recentSearches.map((query, index) => (
                <button
                  key={index}
                  onClick={() => selectRecentSearch(query)}
                  className="rounded-full bg-muted px-3 py-1 text-sm hover:bg-muted/80"
                >
                  {query}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Results */}
      {debouncedQuery.length >= 2 ? (
        <div className="space-y-4">
          {searchLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : !hasResults ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">
                  No results found for &ldquo;{debouncedQuery}&rdquo;
                </p>
              </CardContent>
            </Card>
          ) : (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as SearchType)} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="all">All ({searchResults?.data?.total ?? 0})</TabsTrigger>
                <TabsTrigger value="users">Users ({searchResults?.data?.users?.length ?? 0})</TabsTrigger>
                <TabsTrigger value="posts">Posts ({searchResults?.data?.posts?.length ?? 0})</TabsTrigger>
                <TabsTrigger value="hashtags">Tags ({searchResults?.data?.hashtags?.length ?? 0})</TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="mt-4 space-y-4">
                {/* Users Section */}
                {searchResults?.data?.users && searchResults.data.users.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Users className="h-4 w-4" />
                      People
                    </h3>
                    <div className="space-y-2">
                      {searchResults.data.users.slice(0, 3).map((user: any, index: number) => (
                        <UserResultCard key={user.id} user={user} index={index} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Posts Section */}
                {searchResults?.data?.posts && searchResults.data.posts.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <FileText className="h-4 w-4" />
                      Posts
                    </h3>
                    <div className="space-y-2">
                      {searchResults.data.posts.slice(0, 3).map((post: any, index: number) => (
                        <PostResultCard key={post.id || post.post?.post?.id} post={post} index={index} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Hashtags Section */}
                {searchResults?.data?.hashtags && searchResults.data.hashtags.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Hash className="h-4 w-4" />
                      Hashtags
                    </h3>
                    <div className="space-y-2">
                      {searchResults.data.hashtags.slice(0, 3).map((hashtag: Hashtag, index: number) => (
                        <HashtagResultCard key={hashtag.hashtag} hashtag={hashtag} index={index} />
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="users" className="mt-4 space-y-2">
                {searchResults?.data?.users?.map((user: any, index: number) => (
                  <UserResultCard key={user.id} user={user} index={index} />
                ))}
              </TabsContent>

              <TabsContent value="posts" className="mt-4 space-y-2">
                {searchResults?.data?.posts?.map((post: any, index: number) => (
                  <PostResultCard key={post.id || post.post?.post?.id} post={post} index={index} />
                ))}
              </TabsContent>

              <TabsContent value="hashtags" className="mt-4 space-y-2">
                {searchResults?.data?.hashtags?.map((hashtag: Hashtag, index: number) => (
                  <HashtagResultCard key={hashtag.hashtag} hashtag={hashtag} index={index} />
                ))}
              </TabsContent>
            </Tabs>
          )}
        </div>
      ) : (
        <>
          {/* Trending Hashtags */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="h-5 w-5" />
                Trending Hashtags
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trendingLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : !trendingHashtags?.data || trendingHashtags.data.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No trending hashtags yet. Be the first to start a trend!
                </p>
              ) : (
                <div className="space-y-3">
                  {trendingHashtags.data.map((hashtag: Hashtag, index: number) => (
                    <a
                      key={hashtag.hashtag}
                      href={`/explore?q=%23${hashtag.hashtag}`}
                      className="flex items-center justify-between rounded-lg p-2 transition-colors hover:bg-muted"
                    >
                      <div>
                        <p className="font-medium">#{hashtag.hashtag}</p>
                        <p className="text-xs text-muted-foreground">
                          {hashtag.count.toLocaleString()} posts
                        </p>
                      </div>
                      <span className="text-sm text-muted-foreground">#{index + 1}</span>
                    </a>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

function UserResultCard({ user, index }: { user: any; index: number }) {
  const displayName = user.displayName || user.display_name || user.username
  const avatarUrl = user.avatarUrl || user.avatar_url

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <a
        href={`/profile/${user.username}`}
        className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-muted"
      >
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage src={avatarUrl} />
            <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{displayName}</p>
            <p className="text-sm text-muted-foreground">@{user.username}</p>
            {user.bio && <p className="mt-1 text-sm text-muted-foreground line-clamp-1">{user.bio}</p>}
          </div>
        </div>
        <FollowButton
          userId={user.id}
          username={user.username}
          isFollowing={false}
          size="sm"
          variant="outline"
        />
      </a>
    </motion.div>
  )
}

function PostResultCard({ post, index }: { post: any; index: number }) {
  const author = post.author || post.post?.author
  const content = post.content || post.post?.post?.content || post.post?.content
  const createdAt = post.created_at || post.post?.post?.created_at || post.post?.created_at
  const postId = post.id || post.post?.post?.id

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <a
        href={`/posts/${postId}`}
        className="block rounded-lg border p-4 transition-colors hover:bg-muted"
      >
        <div className="flex items-start gap-3">
          {author && (
            <Avatar className="h-8 w-8">
              <AvatarImage src={author.avatarUrl || author.avatar_url} />
              <AvatarFallback>
                {getInitials(author.displayName || author.display_name || author.username)}
              </AvatarFallback>
            </Avatar>
          )}
          <div className="flex-1 min-w-0">
            {author && (
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-sm">
                  {author.displayName || author.display_name || author.username}
                </span>
                <span className="text-xs text-muted-foreground">@{author.username}</span>
                {createdAt && (
                  <span className="text-xs text-muted-foreground">
                    · {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
                  </span>
                )}
              </div>
            )}
            {content && <p className="text-sm line-clamp-2">{content}</p>}
          </div>
        </div>
      </a>
    </motion.div>
  )
}

function HashtagResultCard({ hashtag, index }: { hashtag: Hashtag; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <a
        href={`/explore?q=%23${hashtag.hashtag}`}
        className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-muted"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <Hash className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-medium">#{hashtag.hashtag}</p>
            <p className="text-sm text-muted-foreground">
              {hashtag.count.toLocaleString()} {hashtag.count === 1 ? 'post' : 'posts'}
            </p>
          </div>
        </div>
      </a>
    </motion.div>
  )
}
